import React from 'react';
import { Activity, AlertTriangle } from 'lucide-react';

const isSameUTCDay = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.getUTCFullYear() === now.getUTCFullYear()
        && d.getUTCMonth() === now.getUTCMonth()
        && d.getUTCDate() === now.getUTCDate();
};

const fmtTime = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const hoursAgo = (iso) => {
    if (!iso) return null;
    return (Date.now() - new Date(iso).getTime()) / 3_600_000;
};

/**
 * Pill rendered on Sales/BD/CS/Mentor Kanban cards to surface the last
 * activity timestamp at a glance. Four visual states:
 *   • emerald "Today"          → activity in the current UTC day
 *   • amber "Stale Xd"         → no activity in 72h+ (only for ACTIVE stages)
 *   • muted "Touched Mar 12…"  → recent but not today
 *   • rose "No activity"       → record exists but has never been touched
 *
 * `stage` (optional): when supplied, the badge will NEVER render the
 * amber "Stale" tone for terminal stages (`enrolled`, `rejected`,
 * `not_interested`, `closed_won`, `closed_lost`). Those records are
 * conceptually finished — flagging them as stale because nobody has
 * touched them since enrollment is misleading and clutters the UI.
 * Terminal records still show their timestamp neutrally so the info is
 * available but not alarming.
 *
 * Renders unconditionally for active stages so missing activity stays
 * visible — silently hiding the pill made it easy to forget about records
 * that had never been contacted at all.
 */
const TERMINAL_STAGES = new Set([
    'enrolled', 'rejected', 'not_interested', 'closed_won', 'closed_lost',
]);

export const LastActivityBadge = ({ at, by, stage, compact = false, testId }) => {
    const today = isSameUTCDay(at);
    const ago = hoursAgo(at);
    const isTerminal = !!stage && TERMINAL_STAGES.has(stage);
    // Enrolled / rejected / etc. records are never "stale" — they're done.
    const stale = !isTerminal && ago !== null && ago >= 72;
    // For terminal records we also suppress the "No activity" alarm because
    // an enrolled lead with no post-enrollment touches is normal.
    const missing = !at && !isTerminal;

    let tone, Icon, label, title;
    if (missing) {
        tone = 'bg-rose-500/10 text-rose-600 ring-rose-500/30';
        Icon = AlertTriangle;
        label = 'No activity';
        title = 'No activity recorded yet';
    } else if (today) {
        tone = 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30';
        Icon = Activity;
        label = 'Today';
        title = by ? `Last activity: ${fmtTime(at)} by ${by}` : `Last activity: ${fmtTime(at)}`;
    } else if (stale) {
        tone = 'bg-amber-500/10 text-amber-700 ring-amber-500/30';
        Icon = AlertTriangle;
        const days = Math.floor(ago / 24);
        label = `Stale ${days}d`;
        title = by ? `Last activity: ${fmtTime(at)} by ${by} — ${days}d ago` : `Last activity: ${fmtTime(at)} — ${days}d ago`;
    } else {
        tone = 'bg-muted text-muted-foreground ring-border';
        Icon = Activity;
        label = at ? fmtTime(at) : '—';
        title = at
            ? (by ? `Last activity: ${fmtTime(at)} by ${by}` : `Last activity: ${fmtTime(at)}`)
            : (isTerminal ? 'No follow-up activity needed' : '');
    }

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone}`}
            title={title}
            data-testid={testId}
        >
            <Icon className="h-3 w-3" />
            {compact || missing || today || stale ? label : `Touched ${label}`}
        </span>
    );
};

export default LastActivityBadge;
